import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import type { GridApi, GridColDef, GridRowId, GridValidRowModel } from '@mui/x-data-grid-premium'

export type ExportRow = GridValidRowModel & {
  id: GridRowId
  path?: readonly string[]
  programName?: string
  programCode?: string
  operator?: unknown
  [key: string]: unknown
}

export type ExportSheet =
  | {
      kind: 'grid'
      sheetName: string
      apiRef: React.MutableRefObject<GridApi>
      order?: number
      rowSpanFields?: string[]
      rowSpanGroupByField?: string
    }
  | {
      kind: 'static'
      sheetName: string
      order?: number
      render: (ws: ExcelJS.Worksheet) => void
    }

type Column = GridColDef<ExportRow>

const XLSX_SHEET_NAME_MAX_LENGTH = 31

function safeSheetName(name: string) {
  return name.replace(/[[\]\\/:?*]/g, '').slice(0, XLSX_SHEET_NAME_MAX_LENGTH) || 'Sheet'
}

function styleWorksheet(ws: ExcelJS.Worksheet, opts?: { zebra?: boolean }) {
  const zebra = opts?.zebra ?? true

  const headerRow = ws.getRow(1)
  headerRow.height = 20

  headerRow.eachCell((cell) => {
    cell.font = { bold: true }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  ws.views = [{ state: 'frozen', ySplit: 1 }]
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columnCount } }

  if (!zebra) return

  for (let r = 2; r <= ws.rowCount; r++) {
    if (r % 2 === 0) {
      ws.getRow(r).eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } }
      })
    }
  }
}

function getVisibleColumns(apiRef: React.MutableRefObject<GridApi>): Column[] {
  const visible = apiRef.current.getVisibleColumns() as unknown as Column[]
  return visible.filter((c) => c.field !== '__check__' && c.field !== '__tree_data_group__')
}

function getVisibleRows(apiRef: React.MutableRefObject<GridApi>): ExportRow[] {
  const ids = apiRef.current.getSortedRowIds()
  return ids
    .map((id) => apiRef.current.getRow(id))
    .filter((r): r is ExportRow => r != null && (r as ExportRow).id != null)
}

/**
 * MUI has had two signatures over time:
 * - positional: (value, row, colDef, apiRef)
 * - object param: ({ value, row, field, colDef, apiRef })
 */
type LegacyParams = {
  value: unknown
  row: ExportRow
  field: string
  colDef: Column
  apiRef: React.MutableRefObject<GridApi>
}

function callColFn(
  fn: unknown,
  value: unknown,
  row: ExportRow,
  colDef: Column,
  apiRef: React.MutableRefObject<GridApi>
): unknown {
  if (typeof fn !== 'function') return value

  // positional
  try {
    return (fn as (v: unknown, r: ExportRow, c: Column, a: React.MutableRefObject<GridApi>) => unknown)(
      value,
      row,
      colDef,
      apiRef
    )
  } catch {
    // legacy object param
    try {
      const params: LegacyParams = { value, row, field: colDef.field, colDef, apiRef }
      return (fn as (p: LegacyParams) => unknown)(params)
    } catch {
      return value
    }
  }
}

function computeDisplayedValue(
  apiRef: React.MutableRefObject<GridApi>,
  col: Column,
  row: ExportRow
): string | number | boolean | Date {
  const raw = row[col.field]
  const gotten = callColFn(col.valueGetter, raw, row, col, apiRef)
  const formatted = col.valueFormatter ? callColFn(col.valueFormatter, gotten, row, col, apiRef) : gotten

  if (formatted == null) return ''
  if (formatted instanceof Date) return formatted
  if (typeof formatted === "string" || typeof formatted === "number" || typeof formatted === "boolean") return formatted
  return String(formatted)
}

function autoSizeColumns(ws: ExcelJS.Worksheet, maxScanRows = 200) {
  ws.columns?.forEach((column) => {
    const values = column.values ?? []
    const sample = values.slice(1, Math.min(values.length, maxScanRows + 1))
    const maxLen = sample.reduce<number>((m, v) => Math.max(m, String(v ?? '').length), 0)
    column.width = Math.max(12, Math.min(60, maxLen + 2))
  })
}

export async function exportAllSheetsToWorkbook(sheets: ExportSheet[], fileName = 'export.xlsx') {
  const wb = new ExcelJS.Workbook()
  const sorted = [...sheets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  for (const sheet of sorted) {
    const ws = wb.addWorksheet(safeSheetName(sheet.sheetName))

    if (sheet.kind === 'static') {
      sheet.render(ws)
      continue
    }

    const cols = getVisibleColumns(sheet.apiRef)
    const rows = getVisibleRows(sheet.apiRef)

    ws.addRow(cols.map((c) => c.headerName ?? c.field))

    const isProgramExclusions = sheet.sheetName === 'Program Exclusions'
    const isTopLevelProgramRow = (row: ExportRow) => typeof row.programName === 'string' && row.programName.trim() !== ''

    for (const row of rows) {
      const values = cols.map((c) => computeDisplayedValue(sheet.apiRef, c, row))
      const excelRow = ws.addRow(values)

      if (isProgramExclusions && isTopLevelProgramRow(row)) {
        excelRow.font = { ...(excelRow.font ?? {}), bold: true }
        excelRow.height = 22
        excelRow.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } }
        })
      }
    }

    if (sheet.rowSpanFields?.length && sheet.rowSpanGroupByField) {
      applyGroupedRowSpans(ws, sheet.apiRef, cols, rows, {
        fields: sheet.rowSpanFields,
        groupByField: sheet.rowSpanGroupByField,
        isBarrierRow: isProgramExclusions ? isTopLevelProgramRow : undefined,
      })
    }

    autoSizeColumns(ws)
    styleWorksheet(ws, { zebra: !isProgramExclusions })
  }

  const buf = await wb.xlsx.writeBuffer()
  saveAs(new Blob([buf]), fileName)
}

function applyGroupedRowSpans(
  ws: ExcelJS.Worksheet,
  apiRef: React.MutableRefObject<GridApi>,
  cols: Column[],
  rows: ExportRow[],
  opts: {
    fields: string[]
    groupByField: string
    isBarrierRow?: (row: ExportRow) => boolean
  }
) {
  const allowed = new Set(opts.fields)
  const firstDataExcelRow = 2 // header is row 1 in this exporter

  const fieldKeys = rows.map((row) => {
    if (opts.isBarrierRow?.(row)) return '__NO_SPAN__'
    const key = row[opts.groupByField]
    return key == null || key === '' ? '__NO_SPAN__' : String(key)
  })

  const operatorKeys = rows.map((row, i) => {
    if (fieldKeys[i] === '__NO_SPAN__') return '__NO_SPAN__'
    return `${fieldKeys[i]}|${row.operator == null ? '' : String(row.operator)}`
  })

  const mergeRun = (col: Column, colIdx: number, runStart: number, runEnd: number) => {
    const excelColIndex = colIdx + 1
    const topRow = firstDataExcelRow + runStart
    const bottomRow = firstDataExcelRow + runEnd

    const topDisplayed = computeDisplayedValue(apiRef, col, rows[runStart])

    // clear below BEFORE merge
    for (let r = topRow + 1; r <= bottomRow; r++) {
      ws.getCell(r, excelColIndex).value = ''
    }

    ws.mergeCells(topRow, excelColIndex, bottomRow, excelColIndex)

    const master = ws.getCell(topRow, excelColIndex)
    master.value = topDisplayed
    master.alignment = { ...(master.alignment ?? {}), vertical: 'top' }
  }

  const mergeRunsForKeys = (keys: string[], field: string) => {
    const colIdx = cols.findIndex((c) => c.field === field)
    if (colIdx === -1) return

    let runStart = 0
    for (let i = 1; i <= keys.length; i++) {
      const isEnd = i === keys.length
      const sameAsPrev = !isEnd && keys[i] === keys[i - 1]
      if (sameAsPrev) continue

      const runEnd = i - 1
      const key = keys[runStart]
      if (runEnd - runStart + 1 > 1 && key !== '__NO_SPAN__') {
        mergeRun(cols[colIdx], colIdx, runStart, runEnd)
      }
      runStart = i
    }
  }

  if (allowed.has('fieldName')) mergeRunsForKeys(fieldKeys, 'fieldName')
  if (allowed.has('operator')) mergeRunsForKeys(operatorKeys, 'operator')
}
